import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { categoriasApi, orcamentosApi } from '../../api/endpoints'
import { Button, IconButton } from '../../components/ui/Button'
import { Input, Toggle } from '../../components/ui/Input'
import { Card, CardHeader, CardBody } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { LoadingState, EmptyState } from '../../components/ui/EmptyState'
import { Modal, ConfirmDialog } from '../../components/ui/Modal'
import { useToast } from '../../components/ui/Toast'
import { PageHeader } from '../../layout/PageHeader'
import { formatMoney, cn } from '../../lib/utils'
import type { Categoria, Orcamento, TagItem } from '../../types'

const COLORS = ['#22C55E', '#3B82F6', '#F59E0B', '#EF4444', '#A78BFA', '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16']
const ICONS = ['🍔', '🏠', '💊', '🎮', '🚗', '📚', '✈️', '🎵', '🏋️', '💼', '🛒', '🎁', '💰', '🐾', '🌿', '📂']

const MES_ATUAL_LABEL = new Date().toLocaleDateString('pt-BR', { month: 'long' })

type CategoriaForm = { nome: string; icone: string; cor: string; parentId: string }

const EMPTY_FORM: CategoriaForm = { nome: '', icone: '📂', cor: COLORS[0], parentId: '' }

type Exclusao = { kind: 'categoria'; item: Categoria } | { kind: 'tag'; item: TagItem }

export function CategoriasPage() {
  const qc = useQueryClient()
  const toast = useToast()
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Categoria | null>(null)
  const [parentNome, setParentNome] = useState<string | null>(null)
  const [form, setForm] = useState<CategoriaForm>(EMPTY_FORM)
  const [tagModal, setTagModal] = useState(false)
  const [tagForm, setTagForm] = useState({ nome: '', cor: COLORS[1] })
  const [erro, setErro] = useState<string | null>(null)
  const [exclusao, setExclusao] = useState<Exclusao | null>(null)
  const [limiteAlvo, setLimiteAlvo] = useState<{ categoria: Categoria; valor: string; alertaAtivo: boolean } | null>(null)

  const hoje = new Date()
  const mes = hoje.getMonth() + 1
  const ano = hoje.getFullYear()

  const { data: categorias = [], isLoading } = useQuery({
    queryKey: ['categorias'],
    queryFn: () => categoriasApi.list({ incluirInativas: false }),
  })
  const { data: tags = [] } = useQuery({ queryKey: ['tags'], queryFn: categoriasApi.listTags })
  const { data: resumo } = useQuery({ queryKey: ['orcamento', mes, ano], queryFn: () => orcamentosApi.getResumo(mes, ano) })

  const orcamentoPorCategoria = new Map<string, Orcamento>((resumo?.categorias ?? []).map((o) => [o.categoriaId, o]))

  const salvarLimite = useMutation({
    mutationFn: () => orcamentosApi.salvar({
      categoriaId: limiteAlvo!.categoria.id, mes, ano,
      limiteValor: Number(limiteAlvo!.valor), alertaAtivo: limiteAlvo!.alertaAtivo,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orcamento'] })
      toast.success('Limite mensal salvo.')
      setLimiteAlvo(null)
    },
    onError: (e: Error) => setErro(e.message),
  })

  const removerLimite = useMutation({
    mutationFn: (orcamentoId: string) => orcamentosApi.remover(orcamentoId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orcamento'] })
      toast.success('Limite removido.')
      setLimiteAlvo(null)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const salvar = useMutation({
    mutationFn: () => editing
      ? categoriasApi.atualizar(editing.id, { nome: form.nome, icone: form.icone, cor: form.cor })
      : categoriasApi.criar({ nome: form.nome, icone: form.icone, cor: form.cor, parentId: form.parentId || undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['categorias'] })
      toast.success(editing ? 'Categoria atualizada.' : 'Categoria criada.')
      setModalOpen(false)
    },
    onError: (e: Error) => setErro(e.message),
  })

  const criarTag = useMutation({
    mutationFn: () => categoriasApi.criarTag({ nome: tagForm.nome.trim(), cor: tagForm.cor }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tags'] })
      toast.success('Tag criada.')
      setTagModal(false)
    },
    onError: (e: Error) => setErro(e.message),
  })

  const excluir = useMutation<unknown, Error, Exclusao>({
    mutationFn: (alvo: Exclusao) => alvo.kind === 'categoria' ? categoriasApi.remover(alvo.item.id) : categoriasApi.removerTag(alvo.item.id),
    onSuccess: (r, alvo) => {
      qc.invalidateQueries({ queryKey: [alvo.kind === 'categoria' ? 'categorias' : 'tags'] })
      // Categoria em uso não é apagada (o histórico ficaria sem categoria): só desativa, e o aviso diz isso.
      toast.success(alvo.kind === 'categoria' ? ((r as { mensagem?: string } | undefined)?.mensagem ?? 'Categoria excluída.') : 'Tag excluída.')
      setExclusao(null)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  function openNew(parent?: Categoria) {
    setEditing(null)
    setParentNome(parent?.nome ?? null)
    setForm({ ...EMPTY_FORM, parentId: parent?.id ?? '', cor: parent?.cor ?? COLORS[0] })
    setErro(null)
    setModalOpen(true)
  }

  function openEdit(c: Categoria) {
    setEditing(c)
    setParentNome(c.parentNome ?? null)
    setForm({ nome: c.nome, icone: c.icone ?? '📂', cor: c.cor ?? COLORS[0], parentId: c.parentId ?? '' })
    setErro(null)
    setModalOpen(true)
  }

  function submit() {
    if (!form.nome.trim()) {
      setErro('Informe o nome da categoria.')
      return
    }
    salvar.mutate()
  }

  function openTagModal() {
    setTagForm({ nome: '', cor: COLORS[1] })
    setErro(null)
    setTagModal(true)
  }

  function abrirLimite(c: Categoria) {
    const atual = orcamentoPorCategoria.get(c.id)
    setLimiteAlvo({ categoria: c, valor: atual ? String(atual.limiteValor) : '', alertaAtivo: atual?.alertaAtivo ?? true })
    setErro(null)
  }

  const renderCategoria = (c: Categoria, depth = 0) => (
    <li key={c.id}>
      <div className={cn('group flex items-center gap-3 px-4 py-2.5 hover:bg-surface2/50', depth > 0 && 'pl-14')}>
        {depth > 0 && <i className="ri-corner-down-right-line -ml-6 text-muted" aria-hidden />}
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded text-lg" style={{ background: `${c.cor ?? '#8B949E'}22` }}>
          {c.icone ?? '📂'}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-white">{c.nome}</p>
          <p className="text-2xs text-muted">
            {c.transacoesCount} lançamento{c.transacoesCount !== 1 ? 's' : ''}
            {depth === 0 && c.subCategorias.length > 0 && ` · ${c.subCategorias.length} subcategoria${c.subCategorias.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <LimiteBadge orcamento={orcamentoPorCategoria.get(c.id)} onClick={() => abrirLimite(c)} />
        <span className="h-2.5 w-2.5 rounded-full" style={{ background: c.cor ?? '#8B949E' }} aria-hidden />
        <div className="flex opacity-100 transition-opacity lg:opacity-0 lg:group-hover:opacity-100 lg:group-focus-within:opacity-100">
          {depth === 0 && <IconButton icon="ri-add-line" label={`Nova subcategoria em ${c.nome}`} tone="primary" onClick={() => openNew(c)} />}
          <IconButton icon="ri-pencil-fill" label={`Editar ${c.nome}`} tone="info" onClick={() => openEdit(c)} />
          <IconButton icon="ri-delete-bin-5-fill" label={`Excluir ${c.nome}`} tone="danger" onClick={() => setExclusao({ kind: 'categoria', item: c })} />
        </div>
      </div>
      {c.subCategorias.length > 0 && <ul>{c.subCategorias.map((s) => renderCategoria(s, depth + 1))}</ul>}
    </li>
  )

  return (
    <>
      <PageHeader title="Categorias" />

      <div className="grid gap-6 xl:grid-cols-[1fr_360px]">
        <Card>
          <CardHeader title={<>Categorias <Badge variant="muted" className="ml-1">{categorias.length}</Badge></>} actions={<Button icon="ri-add-line" onClick={() => openNew()}>Nova categoria</Button>} />
          {isLoading ? (
            <LoadingState />
          ) : categorias.length === 0 ? (
            <EmptyState icon="ri-price-tag-3-line" message="Nenhuma categoria criada." action={<Button variant="soft-primary" icon="ri-add-line" onClick={() => openNew()}>Nova categoria</Button>} />
          ) : (
            <ul data-tutorial="categorias.lista" className="divide-y divide-border/60">{categorias.map((c) => renderCategoria(c))}</ul>
          )}
        </Card>

        <Card className="h-fit">
          <CardHeader title="Tags" actions={<Button size="sm" variant="soft-primary" icon="ri-add-line" onClick={openTagModal}>Nova tag</Button>} />
          <CardBody>
            {tags.length === 0 ? (
              <p className="py-6 text-center text-13 text-muted">Nenhuma tag criada.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {tags.map((t) => (
                  <span key={t.id} className="inline-flex items-center gap-1.5 rounded py-1 pl-2.5 pr-1 text-xs font-medium" style={{ background: `${t.cor ?? '#8B949E'}22`, color: t.cor ?? '#8B949E' }}>
                    {t.nome}
                    <button type="button" onClick={() => setExclusao({ kind: 'tag', item: t })} className="flex h-5 w-5 items-center justify-center rounded hover:bg-black/20" aria-label={`Excluir tag ${t.nome}`}>
                      <i className="ri-close-line" aria-hidden />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar categoria' : parentNome ? `Nova subcategoria em ${parentNome}` : 'Nova categoria'}
        footer={
          <>
            <Button variant="light" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button icon={editing ? 'ri-save-line' : 'ri-add-line'} onClick={submit} loading={salvar.isPending}>{editing ? 'Salvar' : 'Criar'}</Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3 rounded bg-surface2 p-3">
            <span className="flex h-12 w-12 items-center justify-center rounded text-2xl" style={{ background: `${form.cor}22` }}>{form.icone}</span>
            <div>
              <p className="font-medium text-white">{form.nome || 'Nome da categoria'}</p>
              <p className="text-2xs text-muted">Pré-visualização</p>
            </div>
          </div>
          <Input label="Nome" value={form.nome} onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))} placeholder="Ex: Alimentação" autoFocus />
          <div>
            <span className="form-label">Ícone</span>
            <div className="flex flex-wrap gap-1.5">
              {ICONS.map((ic) => (
                <button key={ic} type="button" onClick={() => setForm((f) => ({ ...f, icone: ic }))} aria-pressed={form.icone === ic} aria-label={`Ícone ${ic}`}
                  className={cn('flex h-9 w-9 items-center justify-center rounded text-lg transition-colors', form.icone === ic ? 'bg-primary/15 ring-1 ring-primary' : 'bg-surface2 hover:bg-surface3')}>
                  {ic}
                </button>
              ))}
            </div>
          </div>
          <ColorPicker value={form.cor} onChange={(cor) => setForm((f) => ({ ...f, cor }))} />
          {erro && <p className="flex items-center gap-1.5 rounded bg-expense/10 px-3 py-2 text-13 text-expense" role="alert"><i className="ri-error-warning-line" aria-hidden /> {erro}</p>}
        </div>
      </Modal>

      <Modal
        open={tagModal}
        onClose={() => setTagModal(false)}
        title="Nova tag"
        size="sm"
        footer={
          <>
            <Button variant="light" onClick={() => setTagModal(false)}>Cancelar</Button>
            <Button icon="ri-add-line" onClick={() => tagForm.nome.trim() ? criarTag.mutate() : setErro('Informe o nome da tag.')} loading={criarTag.isPending}>Criar</Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input label="Nome" value={tagForm.nome} onChange={(e) => setTagForm((f) => ({ ...f, nome: e.target.value }))} placeholder="Ex: viagem" autoFocus />
          <ColorPicker value={tagForm.cor} onChange={(cor) => setTagForm((f) => ({ ...f, cor }))} />
          {erro && <p className="flex items-center gap-1.5 rounded bg-expense/10 px-3 py-2 text-13 text-expense" role="alert"><i className="ri-error-warning-line" aria-hidden /> {erro}</p>}
        </div>
      </Modal>

      <Modal
        open={!!limiteAlvo}
        onClose={() => setLimiteAlvo(null)}
        title={`Limite mensal · ${limiteAlvo?.categoria.nome ?? ''}`}
        size="sm"
        footer={
          <>
            {limiteAlvo && orcamentoPorCategoria.has(limiteAlvo.categoria.id) && (
              <Button
                variant="soft-danger"
                icon="ri-delete-bin-line"
                className="mr-auto"
                loading={removerLimite.isPending}
                onClick={() => removerLimite.mutate(orcamentoPorCategoria.get(limiteAlvo.categoria.id)!.id)}
              >
                Remover limite
              </Button>
            )}
            <Button variant="light" onClick={() => setLimiteAlvo(null)}>Cancelar</Button>
            <Button
              icon="ri-save-line"
              loading={salvarLimite.isPending}
              onClick={() => Number(limiteAlvo?.valor) > 0 ? salvarLimite.mutate() : setErro('Informe um limite maior que zero.')}
            >
              Salvar
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-13 text-muted">Vale para {MES_ATUAL_LABEL} de {ano}. Cada mês tem o seu próprio limite.</p>
          <Input
            label="Limite (R$)"
            type="number" min={0} step="0.01"
            value={limiteAlvo?.valor ?? ''}
            onChange={(e) => setLimiteAlvo((l) => l && { ...l, valor: e.target.value })}
            placeholder="0,00"
            autoFocus
          />
          <div className="rounded bg-surface2 p-3">
            <Toggle
              label="Alertar ao chegar em 80% do limite"
              checked={limiteAlvo?.alertaAtivo ?? true}
              onChange={(v) => setLimiteAlvo((l) => l && { ...l, alertaAtivo: v })}
            />
          </div>
          {limiteAlvo && orcamentoPorCategoria.has(limiteAlvo.categoria.id) && (
            <p className="text-13 text-muted">
              Gasto neste mês: <strong className="text-white">{formatMoney(orcamentoPorCategoria.get(limiteAlvo.categoria.id)!.gastoAtual)}</strong>
            </p>
          )}
          {erro && <p className="flex items-center gap-1.5 rounded bg-expense/10 px-3 py-2 text-13 text-expense" role="alert"><i className="ri-error-warning-line" aria-hidden /> {erro}</p>}
        </div>
      </Modal>

      <ConfirmDialog
        open={!!exclusao}
        onClose={() => setExclusao(null)}
        onConfirm={() => exclusao && excluir.mutate(exclusao)}
        loading={excluir.isPending}
        message={
          exclusao?.kind === 'categoria'
            ? <>A categoria <strong className="text-white">{exclusao.item.nome}</strong> deixará de aparecer nas listas.</>
            : <>A tag <strong className="text-white">{exclusao?.item.nome}</strong> será removida de todos os lançamentos.</>
        }
      />
    </>
  )
}

function LimiteBadge({ orcamento, onClick }: { orcamento?: Orcamento; onClick: () => void }) {
  if (!orcamento) {
    return (
      <button type="button" onClick={onClick} className="hidden whitespace-nowrap rounded border border-dashed border-border px-2 py-1 text-2xs text-muted hover:border-primary hover:text-primary sm:block">
        <i className="ri-add-line" aria-hidden /> Limite mensal
      </button>
    )
  }

  const tone = orcamento.percentual >= 100
    ? 'border-expense/40 text-expense'
    : orcamento.percentual >= 80
      ? 'border-warning/40 text-warning'
      : 'border-border text-muted'

  return (
    <button type="button" onClick={onClick} className={cn('hidden whitespace-nowrap rounded border px-2 py-1 text-2xs hover:border-primary hover:text-primary sm:block', tone)}>
      <i className={orcamento.alertaAtivo ? 'ri-notification-3-line' : 'ri-notification-off-line'} aria-hidden />{' '}
      {formatMoney(orcamento.gastoAtual)} / {formatMoney(orcamento.limiteValor)}
    </button>
  )
}

function ColorPicker({ value, onChange }: { value: string; onChange: (cor: string) => void }) {
  return (
    <div>
      <span className="form-label">Cor</span>
      <div className="flex flex-wrap gap-2">
        {COLORS.map((c) => (
          <button key={c} type="button" onClick={() => onChange(c)} aria-pressed={value === c} aria-label={`Cor ${c}`} style={{ background: c }}
            className={cn('h-7 w-7 rounded-full transition-transform', value === c && 'scale-110 ring-2 ring-white ring-offset-2 ring-offset-surface')} />
        ))}
      </div>
    </div>
  )
}
